/**
 * <predictive-search> — accessible search overlay.
 *
 * - Opens via any [data-action="search-open"] click anywhere in the document.
 * - Closes via [data-action="search-close"], Escape, or backdrop click.
 * - Calls Shopify's /search/suggest.json with debounce; renders products,
 *   articles and query suggestions into the live region.
 * - Keyboard: ↑/↓ navigate, Enter opens highlighted row, Tab respects DOM order.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-predictive-dialog]');
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.tplProduct = this.querySelector('[data-predictive-product-template]');
    this.tplArticle = this.querySelector('[data-predictive-article-template]');
    this.tplQuery = this.querySelector('[data-predictive-query-template]');

    this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
    });

    this._debounce = null;
    this._activeIndex = -1;

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-action="search-open"]');
      if (opener) { e.preventDefault(); this.open(); }
    });

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
      if (e.target.closest('[data-action="search-close"]')) this.close();
    });

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
    requestAnimationFrame(() => this.input.focus());
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    this.input.value = '';
    this._activeIndex = -1;
    this.renderHint();
  }

  onInput() {
    const q = this.input.value.trim();
    clearTimeout(this._debounce);
    if (q.length < 2) {
      this.renderHint();
      return;
    }
    this._debounce = setTimeout(() => this.fetchAndRender(q), 180);
  }

  async fetchAndRender(q) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article,query&resources[limit]=5&resources[options][fields]=title,vendor,product_type,tag`;
      const r = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error(`Search failed: ${r.status}`);
      const data = await r.json();
      const res = data.resources && data.resources.results;
      if (!res) { this.renderHint(); return; }
      this.renderResults(res);
    } catch (err) {
      console.warn('[predictive-search]', err);
      this.renderHint();
    }
  }

  renderHint() {
    this.results.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'predictive-search__hint text-muted';
    p.textContent = 'Type to search products, articles and pages.';
    this.results.appendChild(p);
  }

  renderResults(res) {
    this.results.innerHTML = '';
    this._activeIndex = -1;

    const renderGroup = (title, items, renderItem) => {
      if (!items || items.length === 0) return;
      const group = document.createElement('div');
      group.className = 'predictive-search__group';
      const label = document.createElement('span');
      label.className = 'predictive-search__group-label';
      label.textContent = title;
      group.appendChild(label);
      items.forEach((item) => group.appendChild(renderItem(item)));
      this.results.appendChild(group);
    };

    renderGroup('Products', res.products, (p) => this.renderProduct(p));
    renderGroup('Articles', res.articles, (a) => this.renderArticle(a));
    renderGroup('Suggestions', res.queries, (q) => this.renderQuery(q));

    if (this.results.children.length === 0) {
      const p = document.createElement('p');
      p.className = 'predictive-search__hint text-muted';
      p.textContent = 'No results found.';
      this.results.appendChild(p);
    }
  }

  renderProduct(p) {
    const node = this.tplProduct.content.firstElementChild.cloneNode(true);
    node.href = p.url;
    const img = node.querySelector('[data-row-image]');
    if (img && p.image) img.src = p.image;
    else if (img) img.parentElement.style.display = 'none';
    node.querySelector('[data-row-title]').textContent = p.title;
    const price = node.querySelector('[data-row-price]');
    if (price && p.price != null) {
      const cents = typeof p.price === 'string' ? Math.round(parseFloat(p.price) * 100) : p.price;
      price.textContent = this.formatter.format(cents / 100);
    }
    return node;
  }

  renderArticle(a) {
    const node = this.tplArticle.content.firstElementChild.cloneNode(true);
    node.href = a.url;
    node.querySelector('[data-row-title]').textContent = a.title;
    return node;
  }

  renderQuery(q) {
    const node = this.tplQuery.content.firstElementChild.cloneNode(true);
    const term = q.text || q.styled_text || '';
    node.href = `${window.Shopify ? '' : ''}/search?q=${encodeURIComponent(term)}`;
    node.querySelector('[data-row-title]').textContent = term;
    return node;
  }

  onKeydown(e) {
    const rows = Array.from(this.results.querySelectorAll('.predictive-search__row'));
    if (rows.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._activeIndex = (this._activeIndex + 1) % rows.length;
      this._highlight(rows);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._activeIndex = (this._activeIndex - 1 + rows.length) % rows.length;
      this._highlight(rows);
    } else if (e.key === 'Enter') {
      if (this._activeIndex >= 0) {
        e.preventDefault();
        rows[this._activeIndex].click();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }

  _highlight(rows) {
    rows.forEach((row, i) => {
      const active = i === this._activeIndex;
      row.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) row.scrollIntoView({ block: 'nearest' });
    });
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
